import React, { FunctionComponent } from 'react';
import ReactDOM from 'react-dom';
import { useHistory, useParams, Route, Switch, BrowserRouter, Link, StaticRouter } from 'react-router-dom';

type RouteConfig = {
    path: string,
    component: FunctionComponent,
};
type RouterConfig = RouteConfig[];

type TemplateConfig = {
    title?: string,
    keywords?: string,
    description?: string,
    icon?: string,
    viewport?: string,
};

type AyaApp = {
    routes: RouterConfig,
    templateConfig: TemplateConfig,
};

type GetInitialStateContext = {
    common: {
        path: string,
        query: {[key: string]: string},
        params: {[key: string]: string},
    },
}

let routes: RouterConfig = [];

// 存放 ssr-data 的 context
const SSR_DATA_CONTEXT = React.createContext(null);
function useInitialState<T>(): {
    data: T,
    setData: (data: T) => void,
    run: () => void,
    loading: boolean,
    error: boolean,
} {
    let initialState = React.useContext(SSR_DATA_CONTEXT).ssrData;

    // 只有第一个页面才会被初始化 ssr data；之后的页面初始数据都是 null
    if (SSR_ENVIRONMENT === 'window' && SSR_NOT_FIRST_PAGE) initialState = null;

    const [data, setData] = React.useState(initialState);
    const [error, setError] = React.useState(false);
    const [loading, setLoading] = React.useState(false);

    const reactRouterHistory = useHistory();
    const reactRouterParams = useParams();

    const run = async () => {
        try {
            setError(false);
            setLoading(true);
            const { route } = matchRoute(routes, location.pathname);
            // @ts-ignore
            if (route && route.component && route.component.getInitialState) {
                // @ts-ignore
                setData(await route.component.getInitialState({
                    history: reactRouterHistory,
                    location,
                    params: reactRouterParams,
                    common: {
                        path: location.pathname,
                        query: searchToQuery(location.search),
                        params: reactRouterParams,
                    }
                }));
            }
            setLoading(false);
        } catch (e) {
            setError(true);
            setLoading(false);
        }
    };

    React.useEffect(() => {
        // useEffect 只会在 window 环境执行
        if (SSR_NOT_FIRST_PAGE || SSR_MODE === 'csr') run();
    }, []);

    return {
        data,
        setData,
        run,
        loading,
        error,
    };
}

// 构建路由，同时带上 context provider
function makeSwitchPages<T>(routess: RouterConfig, ssrData: T): JSX.Element {
    routes = routess;
    const ProviderWithHistoryListener = () => {
        const history = useHistory();
        React.useEffect(() => {
            if (SSR_ENVIRONMENT === 'window') {
                const unlisten = history.listen(() => {
                    // 前端页面跳转过路由的标记（非首页）
                    SSR_NOT_FIRST_PAGE = true;
                    unlisten();
                });
            }
        }, []);

        const pages: JSX.Element[] = [];
        let Route404 = null;
        for (const route of routes) {
            if (route.path === '/404') {
                Route404 = route;
                continue;
            }
            const { path, component: Page } = route;
            pages.push(
                <Route exact path={path} key={path}>
                    <Page/>
                </Route>
            );
        }
        if (Route404) {
            pages.push(
                <Route exact path='*' key='*'>
                    <Route404.component />
                </Route>
            );
        }

        return (
            <SSR_DATA_CONTEXT.Provider value={{ ssrData }}>
                <Switch>
                    {pages}
                </Switch>
            </SSR_DATA_CONTEXT.Provider>
        );
    };

    return <ProviderWithHistoryListener/>;
}

// 路由匹配，同时计算出 params 参数；找不到返回 /404；404 也有，就返回 null
function matchRoute(routes: RouterConfig, pathname: string): { route: RouteConfig, params: {[key: string]: any} } {
    let params: {[key: string]: any} = {};
    let route404 = null;
    let route = routes.find(route => {
        if (route.path === '/404') route404 = route;
        const routePathArr = route.path.split('\/');
        const pathnameArr = pathname.split('\/');

        if (routePathArr.length === pathnameArr.length) {
            for (let i = 0; i < routePathArr.length; i++) {
                const routePathFragment = routePathArr[i];
                const pathnameFragment = pathnameArr[i];
                if (routePathFragment && routePathFragment[0] === ':') {
                    // 冒号开头的，作为参数
                    params[routePathFragment.substr(1)] = pathnameFragment;
                } else if (routePathFragment !== pathnameFragment) {
                    // 没匹配上，重置 params
                    params = {};
                    return false;
                }
            }
            return true;
        }
        return false;
    });

    return {
        route: route || route404,
        params,
    };
}

// location.search 转成 query 对象
function searchToQuery(search: string): {[key: string]: string} {
    const query: {[key: string]: string} = {};
    if (search) {
        const searchArr = search.substr(1).split('&');
        if (searchArr && searchArr.length) {
            searchArr.forEach(searchKeyValue => {
                const [key, value] = searchKeyValue.split('=');
                if (key) query[key] = value;
            });
        }
    }
    return query;
}

// 前端渲染或者水合工作
function clientInit(routes: RouterConfig) {
    let data = SSR_DATA;
    try {
        data = JSON.parse(SSR_DATA);
    } catch (e) {}

    const switchPages = makeSwitchPages(routes, data);

    const reactRouter = React.createElement(BrowserRouter, {}, switchPages);

    console.log('--- 前端Init SSR_MODE ---');
    console.log(SSR_MODE);
    if (SSR_MODE === 'csr') ReactDOM.render(reactRouter, document.querySelector('#container'));
    ReactDOM.hydrate(reactRouter, document.querySelector('#container'));
}

// 服务端渲染 html；可配置标题，关键词，图标
function renderHtml(reactHtmlStr: string, jsFilePath: string, initialData: any, mode: 'csr' | 'ssr', templateConfig?: TemplateConfig) {
    const { title = 'aya', keywords = '', icon = '', viewport = '', description = '' } = templateConfig || {};
    let dataStr = initialData || null;
    if (typeof initialData === 'object') dataStr = `'${JSON.stringify(initialData)}'`;
    if (typeof initialData === 'string') dataStr = `'${initialData}'`;
    return (`
        <!DOCTYPE html>
        <html lang="en">
            <head>
                <meta charset="UTF-8">
                <title>${title}</title>
                <meta name="keywords" content="${keywords}">
                <meta name="description" content="${description}">
                <meta name="viewport" content="${viewport}">
                <link rel="icon" href="${icon}">
            </head>
            <body>
                <div id="container">${reactHtmlStr}</div>
                <script src="/public/js/common_lib/react.development.js"></script>
                <script type="application/javascript">
                    window.SSR_ENVIRONMENT = 'window';
                    window.SSR_MODE = '${mode}';
                    window.SSR_DATA = ${dataStr};
                    window.SSR_NOT_FIRST_PAGE = false;
                </script>
                <script src="${jsFilePath}"></script>
                <script type="application/javascript">
                    aya_app.default.clientInit(aya_app.default.routes);
                </script>
            </body>
        </html>
    `);
}

// 服务端渲染逻辑；会给 getInitialState 方法回调注入 common.params 参数
async function ssr(
    pathname: string, app: AyaApp, jsFilePath: string, mode: 'csr' | 'ssr',
    ctx: GetInitialStateContext, ReactDOMServer: any
) {
    const { routes, templateConfig } = app;

    if (mode === 'csr') {
        return renderHtml('', jsFilePath, null, 'csr', templateConfig);
    }

    const { params, route } = matchRoute(routes, pathname);

    if (!route) {
        return renderHtml('', jsFilePath, null, 'ssr', templateConfig);
    }

    let ssrData = null;
    // 执行页面的 getInitialState
    // @ts-ignore
    if (route.component.getInitialState) {
        try {
            ctx.common.params = params;
            // @ts-ignore
            ssrData = await route.component.getInitialState(ctx);
        } catch (e) {}
    }

    const switchPages = makeSwitchPages(routes, ssrData);

    const reactRouter = React.createElement(
        StaticRouter,
        // @ts-ignore
        { location: pathname, context: ctx },
        switchPages
    );

    const reactHtmlStr = ReactDOMServer.renderToString(reactRouter);

    return renderHtml(reactHtmlStr, jsFilePath, ssrData, 'ssr', templateConfig);
}

export {
    RouterConfig,
    useInitialState,
    makeSwitchPages,
    matchRoute,
    searchToQuery,
    clientInit,
    useHistory,
    useParams,
    Link,
    GetInitialStateContext,
    StaticRouter,
    renderHtml,
    AyaApp,
    ssr,
};
